import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Spin, App, Row, Col, Select, Result } from 'antd';
import { KEYS } from "../../config/keys";
import { POST, GET } from "../../utils/apicalls";
import { API } from "../../config/api";
import jsPDF from 'jspdf';
import logoBase64 from '../../assets/logo-PNG.png'; // Adjust the path to your logo
import 'jspdf-autotable';
import autoTable from "jspdf-autotable";
import { CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons';

interface EnrollModalProps {
    visible: boolean;
    onClose: () => void;
    courseId: string | number | null;
}

declare global {
    interface Window {
        Razorpay: any;
    }
}

const EnrollModal: React.FC<EnrollModalProps> = ({ visible, onClose, courseId }) => {
    const [form] = Form.useForm();
    const [courseDetails, setCourseDetails] = useState<any>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const { notification } = App.useApp();
    const [courseData, setCoursedata] = useState<any>([]);
    const [couponCode, setCouponCode] = useState<string>('');
    const [discountAmount, setDiscountAmount] = useState<number>(0);
    const [finalAmount, setFinalAmount] = useState<number>(0);
    const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
    const [receiptData, setReceiptData] = useState<any>(null);
    const [downloadSuccess, setDownloadSuccess] = useState<boolean>(false);

    useEffect(() => {
        form.resetFields();
        if (courseId) {
            setLoading(true);
            fetchCourseDetails(courseId)
                .then(data => setCourseDetails(data))
                .catch(error => {
                    console.error("Error fetching course details:", error);
                    notification.error({ message: 'Failed to fetch course details' });
                })
                .finally(() => setLoading(false));
        }
    }, [courseId, form]);

    // Calculate discount and final amount based on form values
    const calculateAmounts = () => {
        const formValues = form.getFieldsValue();
        const baseAmount = parseFloat(formValues.course_price) || 0;
        const discount = (couponCode.trim().toUpperCase() === "MGA45") ? baseAmount * 0.45 : 0;
        setDiscountAmount(discount);
        setFinalAmount(baseAmount - discount);
    };

    // Recalculate when coupon code changes
    useEffect(() => {
        calculateAmounts();
    }, [couponCode]);

    // Watch for form field changes to recalculate amounts
    const handleFormChange = () => {
        calculateAmounts();
    };

    const fetchCourseDetails = async (id: string | number) => {
        return new Promise(resolve => {
            setTimeout(() => {
                resolve({ title: `Course #${id}` });
            }, 500);
        });
    };

    const generateReceiptPDF = (data: any, courseDetails: any) => {
        const doc = new jsPDF();

        const primaryColor: [number, number, number] = [23, 31, 75]; // #171F4B
        const secondaryColor: [number, number, number] = [196, 153, 90]; // #C4995A
        const black: [number, number, number] = [0, 0, 0];

        // === Draw centered logo with 30% opacity ===
        const logoOpacity = doc.GState({ opacity: 0.3 });
        doc.setGState(logoOpacity);

        const pageWidth = doc.internal.pageSize.getWidth();
        const logoWidth = 100;
        const logoHeight = 100;
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 140;
        doc.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, logoHeight);

        // === Reset opacity to 100% ===
        const fullOpacity = doc.GState({ opacity: 1.0 });
        doc.setGState(fullOpacity);

        // === Header ===
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(black[0], black[1], black[2]);
        doc.text("Meantoir Global Academy", 14, 20);

        doc.setFillColor(...primaryColor);
        doc.rect(14, 25, 182, 10, "F");
        doc.setFontSize(12);
        doc.setTextColor(255, 255, 255);
        doc.text(`Receipt #: ${data.razorpay_payment_id}`, 18, 32);
        doc.text(`Date: ${new Date().toLocaleDateString()}`, 150, 32);

        // === Billing Info ===
        doc.setTextColor(black[0], black[1], black[2]);
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        doc.text("BILL TO:", 14, 45);
        doc.text(`${data.name}`, 14, 51);
        doc.text(`${data.email}`, 14, 56);
        doc.text(`${data.phone}`, 14, 61);
        doc.text(`${data.address || "N/A"}`, 14, 66);

        // === Payment Summary Box ===
        doc.setFillColor(...secondaryColor);
        doc.rect(140, 45, 56, 28, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.text("Amount Paid", 145, 55);
        doc.setFontSize(16);
        doc.text(`Rs. ${data.amount}`, 145, 65);

        // === Build table rows conditionally ===
        const body = [
            ["Course", courseDetails?.course_title || "N/A"],
            ["Base Amount", `Rs ${data.course_price || "0.00"}`],
            ["Payment ID", data.razorpay_payment_id],
            ["Order ID", data.razorpay_order_id],
            ["Payment Status", data.paymentstatus],
        ];

        const coupon = data.couponCode?.toLowerCase();
        if (coupon && coupon !== "none" && data.discountAmount > 0) {
            body.push(["Coupon Code", data.couponCode]);
            body.push(["Discount Applied", `Rs ${data.discountAmount?.toFixed(2) || "0.00"}`]);
        }

        body.push(["Final Amount", `Rs ${data.amount}`]);

        // === Table ===
        autoTable(doc, {
            startY: 80,
            head: [["Description", "Details"]],
            body: body,
            theme: "grid",
            headStyles: {
                fillColor: black,
                textColor: 255,
                halign: "left",
            },
            bodyStyles: {
                textColor: black,
            },
            styles: {
                fontSize: 11,
            },
        });

        // === Signature ===
        doc.setFontSize(12);
        doc.setTextColor(black[0], black[1], black[2]);
        doc.text("Issued by, signature:", 14, 255);

        doc.setFontSize(13); // Just 1 point larger than previous
        doc.setFont("helvetica", "bolditalic");
        doc.text("Meantoir Global Academy Private Limited", 14, 265);

        // === Footer ===
        doc.setDrawColor(200);
        doc.line(14, 270, 196, 270);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text("Meantoir Global Academy", 14, 278);
        doc.text("Room 103, 1st Floor, Aiswarya Building, SA Road, Valanjambalam, Kochi, PIN: 682016", 14, 284);
        doc.text("+91 9778127404 | www.meantoir.com | info@meantoir.com", 14, 290);
        doc.text("This Receipt is generated by machine", 14, 294);


        return doc;
    };

    const handleDownloadPDF = () => {
        if (receiptData) {
            const doc = generateReceiptPDF(receiptData, courseData);
            doc.save(`Receipt-${receiptData.razorpay_payment_id}.pdf`);
            setDownloadSuccess(true);
        }
    };

    const handleSubmit = async (values: any) => {
        if (!values.course_price || parseFloat(values.course_price) <= 0) {
            notification.error({ message: "Please enter a valid course price!" });
            return;
        }

        setLoading(true);
        try {
            // Calculate final amounts based on current form values
            const baseAmount = parseFloat(values.course_price);
            const discount = (couponCode.trim().toUpperCase() === "MGA45") ? baseAmount * 0.45 : 0;
            const final = baseAmount - discount;

            await bookingDetails(values, final, discount, baseAmount);
            notification.success({ message: "Enrollment submitted successfully!" });

            // Create receipt data for the "Submit Enrollment" option
            const receiptInfo = {
                ...values,
                razorpay_order_id: "DIRECT-" + Date.now(),
                razorpay_payment_id: "DIRECT-" + Math.floor(Math.random() * 1000000),
                amount: final,
                course_price: baseAmount,
                paymentstatus: 'Direct Enrollment',
                couponCode: couponCode,
                discountAmount: discount
            };

            setReceiptData(receiptInfo);
            setShowDownloadModal(true);
        } catch (err) {
            console.error(err);
            notification.error({ message: "Submission failed" });
        } finally {
            setLoading(false);
        }
    };

    const handleModalClick = (e: React.MouseEvent) => e.stopPropagation();
    const handleCancel = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClose();
    };

    const fetchCourse = async () => {
        try {
            setIsLoading(true);
            const response: any = await GET(API.GET_COURSE_BY_ID + courseId, {});
            setCoursedata(response?.data);

            // Set the course price in the form if available
            if (response?.data?.course_price) {
                form.setFieldsValue({ course_price: response.data.course_price });
                calculateAmounts();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCourse();
    }, []);

    const makePayment = async (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        const values = form.getFieldsValue();
        if (!values.name || !values.email || !values.phone) {
            notification.warning({
                message: "Please fill out name, email, and phone before proceeding to payment.",
            });
            return;
        }

        if (!values.course_price || parseFloat(values.course_price) <= 0) {
            notification.error({ message: "Please enter a valid course price!" });
            return;
        }

        const baseAmount = parseFloat(values.course_price);
        const discount = (couponCode.trim().toUpperCase() === "MGA45") ? baseAmount * 0.45 : 0;
        const final = baseAmount - discount;

        try {
            setIsLoading(true);
            const orderResponse: any = await POST(API.RAZORPAY_CREATE_ORDER, {
                amount: final,
            });

            const { orderId, amount, currency } = orderResponse;

            const options = {
                key: KEYS.RAZORPAY_KEY,
                amount,
                currency,
                name: "Meantoir Global Academy",
                description: "Course Payment",
                order_id: orderId,
                handler: async function (response: any) {
                    try {
                        const paymentData = {
                            ...values,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: "",
                            amount: final,
                            course_price: baseAmount,
                            paymentstatus: 'Success',
                            couponCode: couponCode,
                            discountAmount: discount
                        };

                        await bookingDetails(paymentData, final, discount, baseAmount);
                        setReceiptData(paymentData);
                        setShowDownloadModal(true);
                        notification.success({ message: "Payment successful!" });
                    } catch (error) {
                        console.error("Payment success, but saving failed:", error);
                        notification.error({ message: "Payment processed but record saving failed!" });
                    }
                },
                modal: {
                    ondismiss: async function () {
                        const failedData = {
                            ...values,
                            razorpay_order_id: orderId,
                            razorpay_payment_id: '',
                            razorpay_signature: '',
                            amount: final,
                            course_price: baseAmount,
                            paymentstatus: 'Failed',
                        };
                        await bookingDetails(failedData, final, discount, baseAmount);
                        notification.warning({ message: "Payment was cancelled or failed." });
                    }
                },
                notes: {
                    address: values.address || '',
                },
                theme: {
                    color: "#08493e",
                },
                prefill: {
                    name: values.name,
                    email: values.email,
                    contact: values.phone,
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (error) {
            console.error("Error during Razorpay order creation:", error);
            notification.error({ message: "Unable to initiate payment. Please try again later." });
        } finally {
            setIsLoading(false);
        }
    };

    const bookingDetails = async (values: any, finalAmount: number, discountAmount: number, baseAmount: number) => {
        try {
            const submissionData = {
                UserName: values.name,
                UserEmail: values.email,
                UserPhoneNumber: values.phone,
                Useraddress: values.address || '',
                razorpayOrderId: values.razorpay_order_id,
                razorpayPaymentId: values.razorpay_payment_id,
                razorpaySignature: values.razorpay_signature,
                amount: finalAmount,
                baseAmount: baseAmount,
                coupenCode: couponCode,
                discountAmount: discountAmount,
                paymentstatus: values.paymentstatus,
                Course_id: Number(courseId),
            };

            setIsLoading(true);

            const bookingResponse: any = await POST(API.CREATE_BOOKING_DETAILS, submissionData);
            console.log("Full booking response:", bookingResponse);

            if (bookingResponse?.data) {
                notification.success({ message: "Booking created successfully!" });

                const paymentData = {
                    paymentMethod: "Razorpay",
                    cardNumber: "",
                    bookingId: bookingResponse.data.id,
                    razorpayOrderId: values.razorpay_order_id,
                    razorpayPaymentId: values.razorpay_payment_id,
                    razorpaySignature: values.razorpay_signature,
                    paymentamount: finalAmount,
                    baseAmount: baseAmount,
                    discountAmount: discountAmount,
                    paymentstatus: values.paymentstatus,
                    Currency: "INR",
                    transactionfee: "0",
                    paymentGateway: "Razorpay",
                    coupenCode: couponCode,
                    paymentDate: bookingResponse?.createdAt,
                };

                const paymentPostRequest = await POST(API.CREATE_PAYMENT_DETAILS, paymentData);
                console.log("Payment response:", paymentPostRequest);

                if (paymentPostRequest) {
                    notification.success({ message: "Payment recorded successfully!" });
                } else {
                    notification.error({ message: "Payment saving failed" });
                }

                form.resetFields();
            }
        } catch (error) {
            console.error(error);
            notification.error({ message: "Something went wrong! Please try again later" });
        } finally {
            setIsLoading(false);
        }
    };

    const handlePayLater = async () => {
        const values = form.getFieldsValue();
        if (!values.name || !values.email || !values.phone) {
            notification.warning({
                message: "Please fill out name, email, and phone before proceeding.",
            });
            return;
        }

        if (!values.course_price || parseFloat(values.course_price) <= 0) {
            notification.error({ message: "Please enter a valid course price!" });
            return;
        }

        const baseAmount = parseFloat(values.course_price);
        const discount = (couponCode.trim().toUpperCase() === "MGA45") ? baseAmount * 0.45 : 0;

        const final = baseAmount - discount;

        const pendingData = {
            ...values,
            razorpay_order_id: "PAY-LATER-" + Date.now(),
            razorpay_payment_id: "PENDING-" + Math.floor(Math.random() * 1000000),
            razorpay_signature: "",
            amount: final,
            course_price: baseAmount,
            paymentstatus: 'Pending',
            couponCode: couponCode,
            discountAmount: discount
        };

        try {
            setLoading(true);
            await bookingDetails(pendingData, final, discount, baseAmount);
            setReceiptData(pendingData);
            setShowDownloadModal(true);
            notification.success({ message: "Enrolled successfully with 'Pay Later' option." });
        } catch (error) {
            console.error("Pay Later error:", error);
            notification.error({ message: "Enrollment failed. Please try again." });
        } finally {
            setLoading(false);
        }
    };

    const { Option } = Select;
    const prefixSelector = (
        <Form.Item name="prefix" noStyle initialValue="+91">
            <Select style={{ width: 100 }}>
                <Option value="+91">🇮🇳 +91</Option>
                <Option value="+1">🇺🇸 +1</Option>
                <Option value="+44">🇬🇧 +44</Option>
                <Option value="+61">🇦🇺 +61</Option>
                <Option value="+81">🇯🇵 +81</Option>
                <Option value="+86">🇨🇳 +86</Option>
                <Option value="+49">🇩🇪 +49</Option>
                <Option value="+33">🇫🇷 +33</Option>
                <Option value="+39">🇮🇹 +39</Option>
                <Option value="+34">🇪🇸 +34</Option>
                <Option value="+7">🇷🇺 +7</Option>
                <Option value="+55">🇧🇷 +55</Option>
                <Option value="+52">🇲🇽 +52</Option>
                <Option value="+27">🇿🇦 +27</Option>
                <Option value="+82">🇰🇷 +82</Option>
                <Option value="+65">🇸🇬 +65</Option>
                <Option value="+60">🇲🇾 +60</Option>
                <Option value="+66">🇹🇭 +66</Option>
                <Option value="+84">🇻🇳 +84</Option>
                <Option value="+62">🇮🇩 +62</Option>
                <Option value="+63">🇵🇭 +63</Option>
                <Option value="+971">🇦🇪 +971</Option>
                <Option value="+974">🇶🇦 +974</Option>
                <Option value="+966">🇸🇦 +966</Option>
                <Option value="+965">🇰🇼 +965</Option>
                <Option value="+973">🇧🇭 +973</Option>
                <Option value="+968">🇴🇲 +968</Option>
                <Option value="+20">🇪🇬 +20</Option>
                <Option value="+90">🇹🇷 +90</Option>
                <Option value="+92">🇵🇰 +92</Option>
                <Option value="+880">🇧🇩 +880</Option>
                <Option value="+94">🇱🇰 +94</Option>
                <Option value="+977">🇳🇵 +977</Option>
                <Option value="+64">🇳🇿 +64</Option>
                <Option value="+41">🇨🇭 +41</Option>
                <Option value="+31">🇳🇱 +31</Option>
                <Option value="+46">🇸🇪 +46</Option>
                <Option value="+47">🇳🇴 +47</Option>
                <Option value="+45">🇩🇰 +45</Option>
                <Option value="+358">🇫🇮 +358</Option>
                <Option value="+43">🇦🇹 +43</Option>
                <Option value="+32">🇧🇪 +32</Option>
                <Option value="+351">🇵🇹 +351</Option>
                <Option value="+30">🇬🇷 +30</Option>
                <Option value="+48">🇵🇱 +48</Option>
                <Option value="+420">🇨🇿 +420</Option>
                <Option value="+36">🇭🇺 +36</Option>
                <Option value="+40">🇷🇴 +40</Option>
                <Option value="+385">🇭🇷 +385</Option>
                <Option value="+381">🇷🇸 +381</Option>
                <Option value="+386">🇸🇮 +386</Option>
                <Option value="+421">🇸🇰 +421</Option>
                <Option value="+370">🇱🇹 +370</Option>
                <Option value="+371">🇱🇻 +371</Option>
                <Option value="+372">🇪🇪 +372</Option>
                <Option value="+353">🇮🇪 +353</Option>
                <Option value="+354">🇮🇸 +354</Option>
            </Select>
        </Form.Item>
    );

    const closeAllModals = () => {
        setShowDownloadModal(false);
        onClose();
    };

    return (
        <div onClick={handleModalClick}>
            <Modal
                title={courseData ? `Enroll in ${courseData?.course_title}` : "Enroll in Course"}
                open={visible && !showDownloadModal}
                onCancel={handleCancel}
                footer={null}
                destroyOnClose
                maskClosable={false}
                wrapClassName="enrollment-modal-wrapper"
            >
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin />
                    </div>
                ) : (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        onValuesChange={handleFormChange}
                        initialValues={{ name: '', email: '', phone: '', address: '', course_price: '' }}
                    >
                        <Form.Item label="Full Name" name="name" rules={[{ required: true, message: 'Please input your full name!' }]}>
                            <Input placeholder="Enter your full name" />
                        </Form.Item>

                        <Form.Item label="Email" name="email" rules={[
                            { required: true, message: 'Please input your email!' },
                            { type: 'email', message: 'Please input a valid email!' }
                        ]}>
                            <Input placeholder="Enter your email" />
                        </Form.Item>

                        <Form.Item
                            label="Phone Number"
                            name="phone"
                            rules={[{ required: true, message: 'Please input your phone number!' }]}
                        >
                            <Input addonBefore={prefixSelector} placeholder="Enter your phone number" />
                        </Form.Item>

                        <Form.Item label="Address" name="address">
                            <Input.TextArea placeholder="Enter your address (optional)" />
                        </Form.Item>

                        <Form.Item
                            label="Course Price"
                            name="course_price"
                            rules={[
                                { required: true, message: 'Please enter the course price!' },
                                {
                                    validator: (_, value) => {
                                        if (value && parseFloat(value) > 0) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Please enter a valid amount greater than 0!'));
                                    }
                                }
                            ]}
                        >
                            <Input
                                type="number"
                                placeholder="Enter course price"
                                addonBefore="₹"
                                min={0}
                                step={0.01}
                            />
                        </Form.Item>

                        <Form.Item label="Coupon Code (Optional)">
                            <Input
                                placeholder="Enter coupon code (e.g. MGA45)"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value)}
                            />
                        </Form.Item>

                        {discountAmount > 0 && (
                            <div style={{ marginBottom: 16, padding: '10px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
                                <div style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                    🎉 Coupon Applied Successfully!
                                </div>
                                <div style={{ color: '#389e0d', marginTop: '4px' }}>
                                    Discount: ₹{discountAmount.toFixed(2)} (45% off)
                                </div>
                            </div>
                        )}

                        {finalAmount > 0 && (
                            <div style={{ marginBottom: 16, padding: '10px', backgroundColor: '#f0f9ff', border: '1px solid #87ceeb', borderRadius: '6px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 'bold', color: '#1890ff' }}>Final Amount:</span>
                                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
                                        ₹{finalAmount.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        )}

                        <Form.Item>
                            <Row gutter={16} justify="space-between">
                                <Col span={12}>
                                    <Button
                                        type="primary"
                                        onClick={makePayment}
                                        block
                                        loading={loading}
                                        disabled={finalAmount <= 0}
                                    >
                                        Pay ₹{finalAmount.toFixed(2)} via Razorpay
                                    </Button>
                                </Col>
                                <Col span={12}>
                                    <Button
                                        type="default"
                                        onClick={handlePayLater}
                                        block
                                        loading={loading}
                                        disabled={finalAmount <= 0}
                                    >
                                        Pay Later
                                    </Button>
                                </Col>
                            </Row>
                        </Form.Item>

                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                block
                                loading={loading}
                                disabled={finalAmount <= 0}
                            >
                                Submit Enrollment
                            </Button>
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            {/* Download Receipt Modal */}
            <Modal
                title="Enrollment Completed"
                open={showDownloadModal}
                onCancel={closeAllModals}
                footer={null}
                maskClosable={false}
                centered
                width={500}
            >
                <Result
                    status="success"
                    title="Enrollment Successful!"
                    subTitle={
                        <>
                            <p>Your enrollment has been processed successfully.</p>
                            <p style={{ color: "red" }}>Please download the receipt and send it to this number: <a> +91 9778127404</a>. Otherwise, your course entry will not be confirmed.</p>
                            <p>Payment Status: <strong>{receiptData?.paymentstatus}</strong></p>
                            {receiptData?.paymentstatus === 'Pending' && (
                                <p style={{ color: 'orange' }}>You have chosen to pay later. Please complete your payment soon.</p>
                            )}
                            {receiptData?.paymentstatus === 'Success' && (
                                <p style={{ color: 'green' }}>Your payment has been received successfully.</p>
                            )}
                        </>
                    }
                    extra={[
                        <Button
                            type="primary"
                            key="download"
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadPDF}
                            disabled={downloadSuccess}
                        >
                            {downloadSuccess ? "Downloaded" : "Download Receipt"}
                        </Button>,
                        <Button key="close" onClick={closeAllModals}>
                            Close
                        </Button>
                    ]}
                />
                {downloadSuccess && (
                    <div style={{ textAlign: 'center', marginTop: '15px', color: 'green' }}>
                        <CheckCircleOutlined /> Receipt downloaded successfully!
                    </div>
                )}
                <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                    <h4>Enrollment Details:</h4>
                    <p><strong>Name:</strong> {receiptData?.name}</p>
                    <p><strong>Email:</strong> {receiptData?.email}</p>
                    <p><strong>Course:</strong> {courseData?.course_title || "N/A"}</p>
                    <p><strong>Base Amount:</strong> ₹{receiptData?.course_price?.toFixed(2) || "0.00"}</p>
                    {receiptData?.discountAmount > 0 && (
                        <>
                            <p><strong>Coupon Applied:</strong> {receiptData?.couponCode}</p>
                            <p><strong>Discount:</strong> ₹{receiptData?.discountAmount?.toFixed(2)}</p>
                        </>
                    )}
                    <p><strong>Final Amount:</strong> ₹{receiptData?.amount?.toFixed(2) || "0.00"}</p>
                </div>
            </Modal>
        </div>
    );
};

export default EnrollModal;